/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN SESSION INFO & ATTENDANCE API
 * Role: Admin
 * Scope: Tenant-based Individual Session Instance & Attendance Management
 * Standard: Enterprise / MNC Level
 *
 * CHANGES (backwards compatible, existing callers keep working):
 *  - List now accepts optional filters: date_from, date_to, student_id
 *    (search now also matches lesson titles/notes on the server).
 *  - Empty filters are NOT sent, so the plain URL stays
 *    session_info.php?page=1&limit=10&search=
 *  - Create/Update payloads may include `lessons[]` (multi-lesson items).
 *  - Create/Update results expose the server's `summary`
 *    (what was added / updated / removed) and `session_info_id`.
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */

const ADMIN_SESSION_INFO_ENDPOINT =
  "/admin/sessions/session_info.php";

/* =============================================================
   HELPERS
============================================================= */

/**
 * Builds the query params for the list call.
 * page / limit / search are always sent (same as before);
 * filter params are added only when they have a value.
 */
const buildListParams = ({
  page = 1,
  limit = 10,
  search = "",
  date_from = "",
  date_to = "",
  student_id = "",
} = {}) => {
  const params = { page, limit, search };

  if (date_from) params.date_from = date_from; // YYYY-MM-DD
  if (date_to) params.date_to = date_to;       // YYYY-MM-DD
  if (student_id) params.student_id = student_id;

  return params;
};

/* =============================================================
   GET – Fetch Session Info Instances (Paginated / Searchable / Filterable)
   Each item contains:
     - lessons[]           (multi-lesson breakdown, ordered)
     - attendance_roster   ({ active_students, inactive_students })
============================================================= */

export const getAdminSessionInfos = async (
  filters = {},
  options = {}
) => {
  const params = buildListParams(filters);

  try {
    const res = await apiCall(
      "GET",
      ADMIN_SESSION_INFO_ENDPOINT,
      null,
      "Admin - Fetch Session Instances",
      {
        params,
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message || "Failed to fetch session instances",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit: params.limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Instances Error:",
      error
    );

    return {
      success: false,
      message:
        error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Session Instance by ID
   Returns metadata, `lessons[]` and complete `attendance_roster`
============================================================= */

export const getAdminSessionInfoById = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      null,
      "Admin - Fetch Session Instance By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to fetch session instance details",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Session Instance
   Payload: session fields + `lessons[]` + `attendance_roster[]`
   Instructor fallback & auto-roster still handled by the server.
============================================================= */

export const createAdminSessionInfo = async (
  payload,
  options = {}
) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_SESSION_INFO_ENDPOINT,
      payload,
      "Admin - Create Session Instance",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to create session instance",
      };
    }

    return {
      success: true,
      // Server returns session_info_id + summary at the top level
      data:
        res.data ??
        {
          session_info_id: res.session_info_id ?? null,
          summary: res.summary ?? null,
        },
      message:
        res.message ||
        "Session instance created and roster initialized successfully",
    };
  } catch (error) {
    console.error(
      "Admin Create Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Session Instance, Lessons & Attendance
   The server applies only the DIFFERENCE inside one transaction:
     - lessons[]: rows with an id are updated, rows without are
       inserted, stored rows missing from the list are removed
     - attendance_roster[]: new students inserted, changed marks updated
   Omit `lessons` entirely to leave lessons untouched.
============================================================= */

export const updateAdminSessionInfo = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      payload,
      "Admin - Update Session Instance & Roster",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to update session instance details",
      };
    }

    return {
      success: true,
      data:
        res.data ??
        { summary: res.summary ?? null },
      message:
        res.message ||
        "Session instance and roster variations updated successfully",
    };
  } catch (error) {
    console.error(
      "Admin Update Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Cascade Delete Session Instance
   Removes lesson items, attendance and the session in one transaction
============================================================= */

export const deleteAdminSessionInfo = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      null,
      "Admin - Delete Session Instance",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to delete session instance tracking asset",
      };
    }

    return {
      success: true,
      message:
        res.message ||
        "Session instance tracking architecture removed safely",
    };
  } catch (error) {
    console.error(
      "Admin Delete Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};